import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import Topbar from '../PulseSurvey/Topbar';
import { successToast, errorToast } from '../../utils/helper';
import {
  createCompanyCustomOffering,
  updateCompanyCustomOffering,
  getCompanyCustomOfferingById
} from '../../api/offeringApi';

const B2BCreateCustomOffering = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const perkId = searchParams.get('id');
  const isEditMode = !!perkId;
  const userData = JSON.parse(localStorage.getItem('userData')) || {};
  const { slug } = userData;

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEditMode);
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    image: null,
    website: '',
    phone: '',
    description: '',
    other: ''
  });

  const fetchInfo = () => {
    console.log('fetchInfo function in Create Custom Perk page');
  };

  // Load perk data when in edit mode
  useEffect(() => {
    if (isEditMode) {
      loadPerkData();
    } else {
      setPageLoading(false);
    }
  }, [perkId]);

  const loadPerkData = async () => {
    try {
      const response = await getCompanyCustomOfferingById(perkId);
      if (response?.data) {
        const perk = response.data;
        setFormData({
          title: perk.title,
          image: perk.image || null,
          website: perk.website || '',
          phone: perk.phone || '',
          description: perk.description || '',
          other: perk.other || '',
          status: perk.status || 'approved'
        });
        // Set image preview from existing image URL
        if (perk.image) {
          setImagePreview(perk.image);
        }
        setPageLoading(false);
      }
    } catch (error) {
      console.error('Error loading perk:', error);
      errorToast('Failed to load perk data');
      setPageLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      setFormData((prev) => ({
        ...prev,
        image: file
      }));
    }
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: null
    }));
    setImagePreview(null);
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      errorToast('Title is required');
      return false;
    }
    if (!formData.description.trim()) {
      errorToast('Description is required');
      return false;
    }
    return true;
  };

  const prepareFormData = () => {
    const payload = new FormData();
    if (perkId) {
      payload.append('id', perkId);
    }
    payload.append('title', formData.title);
    payload.append('description', formData.description);
    payload.append('website', formData.website);
    payload.append('phone', formData.phone);
    payload.append('other', formData.other);

    // Add image if it's a new file (not a string URL from previous upload)
    if (formData.image instanceof File) {
      payload.append('image', formData.image);
    }

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const payload = prepareFormData();
      let response;

      if (isEditMode) {
        response = await updateCompanyCustomOffering(perkId, payload);
      } else {
        response = await createCompanyCustomOffering(payload);
      }

      if (response?.meta?.code === 1 || response?.meta?.code === 200) {
        successToast(
          isEditMode
            ? 'Custom perk updated successfully!'
            : 'Custom perk created successfully!'
        );
        // Navigate back to perks page
        setTimeout(() => {
          navigate(`/${slug}/offering`);
        }, 1500);
      } else {
        errorToast(response?.message || 'Failed to save perk');     }
    } catch (error) {
      console.error('Error saving perk:', error);
      errorToast(error.message || 'Failed to save custom perk');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="relative lg:mb-[45px]">
        <Helmet>
          <meta charSet="utf-8" />
          <title>
            {isEditMode ? 'Edit Custom Perk' : 'Create Custom Perk'} | Shoorah Admin
          </title>
        </Helmet>
        <Topbar
          title={isEditMode ? 'Edit Custom Perk' : 'Create Custom Perk'}
          infoBtn={false}
          setOpen={setOpen}
          fetchInfo={fetchInfo}
        />

        {/* Back Button */}
        <div className="mt-6 mb-6">
          <button
            onClick={() => navigate(`/${slug}/offering`)}
            className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            <span className="text-sm font-medium">Back to Perks</span>
          </button>
        </div>

        {/* Loading State */}
        {pageLoading && (
          <div className="w-full bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6 flex items-center justify-center min-h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Loading perk...</p>
            </div>
          </div>
        )}

        {/* Form Container */}
        {!pageLoading && (
          <div className="w-full bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter perk title"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors"
                />
              </div>

              {/* Image Upload Field */}
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Image
                </label>
                <div className="flex flex-col space-y-3">
                  {imagePreview ? (
                    <div className="relative w-[300px] h-[300px]  rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute top-0 right-0 text-red-600 text-lg p-1 rounded-full  transition-colors"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div className="border-2 w-[300px] h-[300px] flex items-center justify-center border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 transition-colors">
                      <input
                        type="file"
                        name="image"
                        onChange={handleImageChange}
                        accept="image/*"
                        className="hidden"
                        id="image-upload"
                      />
                      <label
                        htmlFor="image-upload"
                        className="cursor-pointer flex flex-col items-center justify-center"
                      >
                        <svg
                          className="w-8 h-8 text-gray-400 dark:text-gray-500 mb-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Click to upload image
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Website Field */}
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Website
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  placeholder="https://example.com"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors"
                />
              </div>

              {/* Phone Field */}
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 (555) 000-0000"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors"
                />
              </div>

              {/* Description Field */}
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter detailed description of the perk"
                  rows="4"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors resize-none"
                />
              </div>

              {/* Other Field */}
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Other Information
                </label>
                <textarea
                  name="other"
                  value={formData.other}
                  onChange={handleInputChange}
                  placeholder="Any additional information"
                  rows="3"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors resize-none"
                />
              </div>

              {/* Status Field */}
              {/* {isEditMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors"
                  >
                    <option value="draft">Draft</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              )} */}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-shoorah-darkBorder">
                <div className="w-[50%]"></div>
                <button
                  type="button"
                  onClick={() => navigate('/offering')}
                  className="flex-1 px-6 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-2.5 rounded-lg bg-blue-600 dark:bg-blue-700 text-white font-medium hover:bg-blue-700 dark:hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading
                    ? isEditMode
                      ? 'Updating...'
                      : 'Creating...'
                    : isEditMode
                    ? 'Update Perk'
                    : 'Create Perk'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </>
  );
};

export default B2BCreateCustomOffering;
